import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Grid, Box, Typography, Button, Stack, Switch, FormControlLabel, Tooltip, Autocomplete, TextField } from '@mui/material';
import {
  postFacilityOrderContactForm,
  postDeleteFacilityOrderContact,
  postUpdateFacilityOrderContactCc,
} from '../../../../api/facility';
import Iconify from '../../../../reusable-components/iconify';
import FormTextWithValue from '../../../../reusable-components/form/FormTextWithValue';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';

FacilityOrderContact.propTypes = {
  facilityId: PropTypes.any,
  index: PropTypes.number,
  contact: PropTypes.object,
  positions: PropTypes.array,
  getContacts: PropTypes.func,
  setAddContact: PropTypes.func,
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getContactValue = (contact) => ({
  firstName: contact?.firstName || '',
  lastName: contact?.lastName || '',
  position: contact?.position || '',
  email: contact?.email || '',
  phone: contact?.phone || '',
  cell: contact?.cell || '',
});

export default function FacilityOrderContact({
  facilityId,
  index,
  contact = null,
  positions = [],
  getContacts,
  setAddContact,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isSuperAdmin = useScopeCheck(['Super-Admin']);
  const [edit, setEdit] = useState(contact === null);
  const [value, setValue] = useState(getContactValue(contact));
  const [cc, setCc] = useState(contact?.ccOnOrderEmails ?? false);
  const [ccSaving, setCcSaving] = useState(false);

  // Don't overwrite unsaved edits when the contact list refreshes
  useEffect(() => {
    if (!edit) setValue(getContactValue(contact));
  }, [contact, edit]);

  useEffect(() => {
    setCc(contact?.ccOnOrderEmails ?? false);
  }, [contact]);

  // New contacts save the CC setting with the rest of the form; existing contacts save it immediately
  const handleCcChange = async (checked) => {
    setCc(checked);
    if (contact === null) return;
    setCcSaving(true);
    try {
      const response = await postUpdateFacilityOrderContactCc(contact.id, checked);
      if (!response.data) throw new Error();
      enqueueSnackbar(`CC turned ${checked ? 'on' : 'off'} for Contact ${index + 1}`, { variant: 'success' });
    } catch {
      setCc(!checked);
      enqueueSnackbar(`Error updating CC for Contact ${index + 1}`, { variant: 'error' });
    }
    setCcSaving(false);
  };

  const handleEditSave = async () => {
    try {
      const response = await postFacilityOrderContactForm({ ...value, ccOnOrderEmails: cc }, facilityId, contact?.id);
      if (response.data?.success) {
        if (contact === null) setAddContact(false);
        await getContacts();
        setEdit(false);
        enqueueSnackbar(`Successfully ${contact ? 'updated' : 'added'} Contact ${index + 1}`, {
          variant: 'success',
        });
      } else
        enqueueSnackbar(response.data?.message ? `Error: ${response.data.message}` : 'Error saving contact', {
          variant: 'error',
        });
    } catch {
      enqueueSnackbar('Error saving contact', { variant: 'error' });
    }
  };

  const handleCancel = () => {
    if (contact === null) {
      setAddContact(false);
      return;
    }
    setEdit(false);
    setValue(getContactValue(contact));
  };

  const handleDelete = async () => {
    try {
      const response = await postDeleteFacilityOrderContact(contact.id);
      if (!response.data) throw new Error();
      await getContacts();
      enqueueSnackbar(`Successfully deleted Contact ${index + 1}`, { variant: 'success' });
    } catch {
      enqueueSnackbar(`Error deleting Contact ${index + 1}`, { variant: 'error' });
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={2} sx={{ mt: 4 }}>
          <Grid item>
            <Typography variant="h5" color="info.main" display="inline">
              Contact {index + 1}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title="CC this contact on order created, order approved, and status update emails for this facility" arrow>
              <FormControlLabel
                label="CC on Orders"
                disabled={!isSuperAdmin || ccSaving}
                sx={{ mr: 0 }}
                control={
                  <Switch color="info" checked={cc} onChange={(e) => handleCcChange(e.target.checked)} />
                }
              />
            </Tooltip>
          </Grid>
          <ScopeGuard scopes={['Super-Admin']}>
            <Grid item>
              <Box>
                {edit ? (
                  <>
                    <AwaitButton
                      variant="outlined"
                      size="small"
                      color="info"
                      sx={{ mr: 1 }}
                      onClick={handleEditSave}
                      disabled={!emailRegex.test(value.email.trim())}
                    >
                      Save
                    </AwaitButton>
                    <Button variant="outlined" size="small" color="error" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      color="info"
                      startIcon={<Iconify icon="material-symbols:edit" />}
                      sx={{ mr: 1 }}
                      onClick={() => setEdit(true)}
                    >
                      Edit
                    </Button>
                    <ConfirmDialog
                      icon="delete-outline"
                      title="Delete"
                      color="error"
                      content={`Are you sure you want to delete Contact ${index + 1}?`}
                      actionButton="Delete"
                      action={handleDelete}
                    />
                  </>
                )}
              </Box>
            </Grid>
          </ScopeGuard>
        </Grid>
      </Grid>
      <Box sx={{ width: '100%' }}>
        <Grid container direction="row" justifyContent="left" alignItems="flex-start">
          <Grid item xs={12}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <FormTextWithValue
                  value={value}
                  title="First Name"
                  flex={4}
                  name="firstName"
                  setValue={setValue}
                  disabled={!edit}
                  autoComplete="no-autocomplete"
                  id={`facility-contact-first-name-${index}`}
                />
                <FormTextWithValue
                  value={value}
                  title="Last Name"
                  flex={4}
                  name="lastName"
                  setValue={setValue}
                  disabled={!edit}
                  autoComplete="no-autocomplete"
                  id={`facility-contact-last-name-${index}`}
                />
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Position
                    </Typography>
                    <Autocomplete
                      freeSolo
                      autoSelect
                      fullWidth
                      size="small"
                      value={value.position}
                      options={positions}
                      onChange={(_, newValue) => setValue({ ...value, position: newValue ?? '' })}
                      renderInput={(params) => <TextField {...params} placeholder="Select or type a position" autoComplete="off" />}
                      disabled={!edit}
                      id={`facility-contact-position-${index}`}
                    />
                  </Box>
                </Grid>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <FormTextWithValue
                  value={value}
                  title="Email*"
                  flex={4}
                  name="email"
                  setValue={setValue}
                  disabled={!edit}
                  autoComplete="no-autocomplete"
                  id={`facility-contact-email-${index}`}
                />
                <FormTextWithValue
                  value={value}
                  title="Phone"
                  flex={4}
                  name="phone"
                  setValue={setValue}
                  disabled={!edit}
                  autoComplete="no-autocomplete"
                  id={`facility-contact-phone-${index}`}
                />
                <FormTextWithValue
                  value={value}
                  title="Cell"
                  flex={4}
                  name="cell"
                  setValue={setValue}
                  disabled={!edit}
                  autoComplete="no-autocomplete"
                  id={`facility-contact-cell-${index}`}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
